import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPlus,
  faTrash,
  faTable,
  faBolt,
  faEye,
  faFileExport,
  faFileImport,
  faSync,
  faEdit,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import "./styles/QuotaModal.css";
import config from "../../configs/config";
import * as XLSX from 'xlsx';

const STORAGE_KEY = "config_json";
const ACTION_OPTIONS = ["TERMINATE", "CONTINUE"];
const MAX_COMBINATIONS = 5000;

const plain = (str = "") => String(str).replace(/<[^>]*>/g, "").trim();

const isAllowedType = (type = "") => {
  const t = String(type).toLowerCase().replace(/\s/g, "");
  return t === "singleresponse" || t === "multipleresponse" || t === "1" || t === "2";
};

const getQuestionRows = (q) => {
  if (!q) return [];
  if (q.Rows && Array.isArray(q.Rows))
    return q.Rows.map((r) => ({ label: plain(r.title || `Option ${r.index}`), index: r.index }));
  if (q.values && Array.isArray(q.values))
    return q.values.map((v) => ({ label: plain(v.title || `Option ${v.index}`), index: v.index }));
  if (q.rows && Array.isArray(q.rows))
    return q.rows.map((r) => ({ label: plain(r.text || r.title || `Row ${r.index}`), index: r.index }));
  return [];
};

const cartesian = (...arrays) =>
  arrays.reduce(
    (acc, arr) => acc.flatMap((combo) => arr.map((item) => [...combo, item])),
    [[]]
  );

const loadFromStorage = () => {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
};

const saveToStorage = (data) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { }
};

const buildQuotaRows = (selectedVarIds, sdataGroup) => {
  const varDefs = selectedVarIds
    .map((id) => {
      const q = sdataGroup[id];
      if (!q) return null;
      const rows = getQuestionRows(q);
      if (!rows.length) return null;
      const type = String(q.type).toLowerCase().replace(/\s/g, "");
      const isMulti = type === "multipleresponse" || type === "multiple2dgrid" || type === "2";
      return rows.map((row) => ({ ...row, varNum: q.Question_number || id, isMulti }));
    })
    .filter(Boolean);

  if (!varDefs.length) return [];

  const totalCombos = varDefs.reduce((prod, arr) => prod * arr.length, 1);
  if (totalCombos > MAX_COMBINATIONS) {
    alert(`This would create ${totalCombos.toLocaleString()} quota rows. Maximum allowed is ${MAX_COMBINATIONS.toLocaleString()}.`);
    return [];
  }

  return cartesian(...varDefs).map((combo) => ({
    name: combo.map((r) => r.label).join(" × "),
    logic: combo.map((r) => `${r.varNum}${r.isMulti ? " contains " : "="}${r.index}`).join(" And "),
    target: 0,
    action: "CONTINUE",
  }));
};

const actionStyle = (action) => {
  if (action === "TERMINATE")
    return { bg: "#fef2f2", text: "#dc2626", border: "#fca5a5" };
  return { bg: "#f0fdf4", text: "#059669", border: "#6ee7b7" };
};

const typeColor = (t) => {
  const lower = String(t).toLowerCase().replace(/\s/g, "");
  return lower === "singleresponse" || lower === "1" ? "var(--color-brand, #6366f1)" : "#0891b2";
};

const typeLabel = (t) => {
  const lower = String(t).toLowerCase().replace(/\s/g, "");
  return lower === "singleresponse" || lower === "1" ? "Single" : "Multi";
};

// ─── VariableMultiSelect ──────────────────────────────────────────────────────
const VariableMultiSelect = ({ sdataGroup, selectedIds, onToggle }) => {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setTerm(""); }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const allVars = Object.entries(sdataGroup || {})
    .filter(([, q]) => isAllowedType(q.type))
    .map(([id, q]) => ({
      id, qNum: q.Question_number || id, type: q.type || "Single",
      title: plain(q.Qtitle || ""), rowCount: getQuestionRows(q).length,
    }));

  const filtered = allVars.filter(
    (v) => !selectedIds.includes(v.id) &&
      (term === "" ||
        v.qNum.toLowerCase().includes(term.toLowerCase()) ||
        v.title.toLowerCase().includes(term.toLowerCase()))
  );

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div className={`qm-multiselect-trigger ${open ? "open" : ""}`} onClick={() => setOpen(true)}>
        {selectedIds.map((id) => {
          const q = sdataGroup?.[id];
          const qNum = q?.Question_number || id;
          return (
            <span key={id} className="qm-chip" style={{ background: typeColor(q?.type || "") }}>
              {qNum}
              <button className="qm-chip-remove" onClick={(e) => { e.stopPropagation(); onToggle(id); }}>×</button>
            </span>
          );
        })}
        <input
          className="qm-search-input"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selectedIds.length ? "" : "Search Single / Multi Response questions…"}
        />
        <span className="qm-caret">▾</span>
      </div>

      {open && (
        <div className="qm-dropdown">
          {filtered.length === 0 ? (
            <div className="qm-dropdown-empty">
              {term ? "No matching questions" : "No more Single/Multi Response questions available"}
            </div>
          ) : (
            filtered.map((v) => (
              <div key={v.id} className="qm-dropdown-item" onClick={() => { onToggle(v.id); setTerm(""); }}>
                <div>
                  <span className="qm-dropdown-item-title">{v.qNum}</span>
                  {v.title && (
                    <span className="qm-dropdown-item-desc">
                      {v.title.length > 50 ? v.title.slice(0, 50) + "…" : v.title}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="qm-badge" style={{ background: typeColor(v.type) }}>{typeLabel(v.type)}</span>
                  <span className="qm-count-badge">{v.rowCount} opt.</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── QuotaEditor ──────────────────────────────────────────────────────────────
const QuotaEditor = ({ quota, sdataGroup, onChange, onDelete, showDelete, onGenerate, projectId, onBusyChange, refreshTrigger }) => {
  const { name, selectedVarIds = [], rows = [], generated = false, quotaId } = quota;
  const [bulkTarget, setBulkTarget] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(name);
  const [achievedData, setAchievedData] = useState({});
  const [achievedLoading, setAchievedLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMsg, setGeneratingMsg] = useState("");

  const fetchAchieved = async () => {
    if (!projectId) return;
    setAchievedLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/get-quota-tracking/${projectId}`, {
        credentials: 'include',
      });
      const result = await res.json();
      if (result.success) {
        const map = {};
        const tracking = result.tracking || {};
        // tracking is { quotaName: { quotaId, rows: [{name, logic, count}] } }
        Object.values(tracking).forEach(quotaGroup => {
          if (!quotaGroup) return;
          // Filter to this quota's rows by quotaId when possible
          if (quotaId && quotaGroup.quotaId && quotaGroup.quotaId !== quotaId) return;
          const trackRows = Array.isArray(quotaGroup.rows) ? quotaGroup.rows : [];
          trackRows.forEach(row => {
            const key = row.name || row.logic;
            if (key) map[key] = row.count || row.achieved || 0;
          });
        });
        setAchievedData(map);
      }
    } catch (e) { console.error('Achieved fetch error:', e); }
    setAchievedLoading(false);
  };

  // Fetch on generation
  useEffect(() => {
    if (generated) fetchAchieved();
  }, [generated, projectId]);

  // Fetch when refreshTrigger changes (for "Refresh All" functionality)
  useEffect(() => {
    if (generated && refreshTrigger) {
      fetchAchieved();
    }
  }, [refreshTrigger]);

  const handleToggleVar = (id) => {
    const next = selectedVarIds.includes(id)
      ? selectedVarIds.filter((v) => v !== id)
      : [...selectedVarIds, id];

    let newName = quota.name;
    if (quota.autoName !== false) {
      if (next.length > 0) {
        const qNums = next.map((vid) => { const q = sdataGroup[vid]; return q?.Question_number || vid; });
        newName = `Quota_${qNums.join("x")}`;
      }
    }
    onChange({ ...quota, name: newName, selectedVarIds: next, generated: false, rows: [] });
  };

  const handleGenerate = async () => {
    if (!selectedVarIds.length || isGenerating) return;

    // Calculate expected combinations upfront for the status message
    const totalCombos = selectedVarIds.reduce((prod, id) => {
      const q = sdataGroup[id];
      return q ? prod * (getQuestionRows(q).length || 1) : prod;
    }, 1);

    const msg = `Generating ${totalCombos.toLocaleString()} combinations…`;
    setIsGenerating(true);
    onBusyChange?.(true, msg);
    setGeneratingMsg(msg);

    // Yield to the browser so React can flush the loading state before the heavy work
    await new Promise(resolve => setTimeout(resolve, 30));

    try {
      const newRows = buildQuotaRows(selectedVarIds, sdataGroup);
      const updated = { ...quota, rows: newRows, generated: true };
      onChange(updated);
      onGenerate?.(updated);
    } finally {
      setIsGenerating(false);
      setGeneratingMsg("");
      onBusyChange?.(false, '');
    }
  };

  const handleRowChange = (idx, field, value) => {
    const next = rows.map((r, i) =>
      i === idx ? { ...r, [field]: field === "target" ? Number(value) || 0 : value } : r
    );
    onChange({ ...quota, rows: next });
  };

  const handleRemoveRow = (idx) => {
    onChange({ ...quota, rows: rows.filter((_, i) => i !== idx) });
  };

  const handleBulkAction = (action) => {
    onChange({ ...quota, rows: rows.map((r) => ({ ...r, action })) });
  };

  const handleBulkTarget = (val) => {
    const n = Number(val) || 0;
    onChange({ ...quota, rows: rows.map((r) => ({ ...r, target: n })) });
  };

  const handleNameEdit = () => {
    if (isEditingName) {
      onChange({ ...quota, name: editName, autoName: false });
      setIsEditingName(false);
    } else {
      setEditName(name);
      setIsEditingName(true);
    }
  };

  const handleNameChange = (e) => setEditName(e.target.value);

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') { onChange({ ...quota, name: editName, autoName: false }); setIsEditingName(false); }
    if (e.key === 'Escape') { setEditName(name); setIsEditingName(false); }
  };

  const handleAddRow = () => {
    onChange({ ...quota, rows: [...rows, { name: '', logic: '', target: 0, action: 'CONTINUE' }] });
  };

  const totalTarget = rows.reduce((s, r) => s + (r.target || 0), 0);
  const totalCombos = selectedVarIds.reduce((prod, id) => {
    const q = sdataGroup[id];
    if (!q) return prod;
    return prod * (getQuestionRows(q).length || 0);
  }, 1);

  return (
    <div style={{ paddingTop: 4 }}>
      {/* ── Variable Select (only before generated) ── */}
      {!generated && (
        <div className="qm-var-section">
          <label className="qm-label">
            Select questions <span className="qm-label-sub">(only Single &amp; Multi Response)</span>
          </label>
          <VariableMultiSelect sdataGroup={sdataGroup} selectedIds={selectedVarIds} onToggle={handleToggleVar} />
        </div>
      )}

      {/* ── Preview box + Remove ── */}
      {selectedVarIds.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div className="qm-preview-box" style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
            <span className="qm-preview-label">
              <FontAwesomeIcon icon={faTable} style={{ color: "var(--color-brand,#6366f1)" }} />
              &nbsp; Quota: </span>
            {isEditingName ? (
              <input
                type="text"
                className="qm-input qm-name-edit-input"
                value={editName}
                onChange={handleNameChange}
                onKeyDown={handleNameKeyDown}
                onBlur={() => { onChange({ ...quota, name: editName, autoName: false }); setIsEditingName(false); }}
                autoFocus
                style={{ flex: 1, fontSize: 14, fontWeight: 600, padding: "2px 6px" }}
              />
            ) : (
              <span
                className="qm-preview-quota-name"
                onClick={() => { setEditName(name); setIsEditingName(true); }}
                title="Click to edit name"
                style={{ cursor: "pointer", borderBottom: "1px dashed #94a3b8" }}
              >
                {name}
              </span>
            )}
            {isEditingName ? (
              <button className="qm-name-edit-btn" onClick={handleNameEdit} title="Save">
                <FontAwesomeIcon icon={faCheck} size="xs" />
              </button>
            ) : (
              <span className="qm-preview-total">({generated ? rows.length.toLocaleString() : totalCombos.toLocaleString()} {generated ? 'rows' : 'combos'})</span>
            )}
          </div>
          {showDelete && (
            <button className="qm-remove-btn" onClick={() => {
              if (window.confirm(`Remove quota "${name}"?`)) onDelete();
            }}>
              <FontAwesomeIcon icon={faTrash} size="xs" /> Remove
            </button>
          )}
        </div>
      )}

      {/* ── Generate button (before generated) ── */}
      {!generated && (
        <div>
          <button
            className={`qm-generate-btn ${selectedVarIds.length && !isGenerating ? "enabled" : "disabled"}`}
            onClick={handleGenerate}
            disabled={!selectedVarIds.length || isGenerating}
          >
            <FontAwesomeIcon icon={isGenerating ? faSync : faBolt} spin={isGenerating} />
            {isGenerating ? "Generating…" : "Generate Quota Config"}
          </button>
          {isGenerating && generatingMsg && (
            <p className="qm-generating-msg">{generatingMsg}</p>
          )}
        </div>
      )}

      {/* ── Generated Table ── */}
      {generated && (
        <div>
          {/* Table header row */}
          <div className="qm-table-header-row">
            <div className="qm-table-title-group">
              {/* <span className="qm-table-title">{name}</span> */}
              {/* <span className="qm-rows-badge">{rows.length} rows</span> */}
              {/* Refresh achieved counts button — replaces "View Counts" */}
              <button
                className="qm-view-counts-btn"
                onClick={fetchAchieved}
                disabled={achievedLoading}
                title="Refresh achieved counts"
              >
                <FontAwesomeIcon icon={faSync} spin={achievedLoading} />
                {achievedLoading ? " Loading…" : " Refresh"}
              </button>
            </div>

            <div className="qm-bulk-controls">
              <span className="qm-bulk-label">Bulk set:</span>
              <input
                type="number" min={0} placeholder="Target"
                className="qm-bulk-input" value={bulkTarget}
                onChange={(e) => setBulkTarget(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { handleBulkTarget(e.target.value); setBulkTarget(""); } }}
              />
              <button
                className="qm-bulk-btn apply" disabled={bulkTarget === ""}
                onClick={() => { if (bulkTarget === "") return; handleBulkTarget(bulkTarget); setBulkTarget(""); }}
              >Apply</button>
              {ACTION_OPTIONS.map((a) => (
                <button key={a} className={`qm-bulk-btn ${a.toLowerCase()}`} onClick={() => handleBulkAction(a)}>
                  All {a}
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="qm-empty">No combinations generated. Ensure selected questions have rows.</div>
          ) : (
            <div className="qm-table-wrap">
              <table className="qm-table">
                <colgroup>
                  <col style={{ width: 40 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 50 }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "linear-gradient(135deg,var(--color-brand,#6366f1) 0%,var(--color-brand-end,#8b5cf6) 100%)" }}>
                    {["#", "Name", "Logic", "Target", "Achieved", "Action", ""].map((h, i) => (
                      <th key={h || `col-${i}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const ac = actionStyle(row.action);
                    const achieved = achievedData[row.name] ?? achievedData[row.logic] ?? null;
                    const isFull = achieved !== null && row.target > 0 && achieved >= row.target;

                    return (
                      <tr key={idx} style={isFull ? { background: "#f0fdf4" } : {}}>
                        <td className="qm-td-num">{idx + 1}</td>
                        <td className="qm-td-name">
                          <input
                            type="text"
                            value={row.name}
                            className="qm-td-input"
                            placeholder="Row name"
                            onChange={(e) => handleRowChange(idx, "name", e.target.value)}
                          />
                        </td>
                        <td className="qm-td-logic">
                          <input
                            type="text"
                            value={row.logic}
                            className="qm-td-input"
                            placeholder="e.g. Q1=1"
                            onChange={(e) => handleRowChange(idx, "logic", e.target.value)}
                          />
                        </td>

                        {/* Target — green pill if quota full, else editable input */}
                        <td>
                          {isFull ? (
                            <div style={{
                              background: '#dcfce7',
                              color: '#15803d',
                              border: '1.5px solid #86efac',
                              borderRadius: 6,
                              padding: '4px 8px',
                              fontWeight: 700,
                              textAlign: 'center',
                              fontSize: 13,
                            }}>
                              {row.target}
                            </div>
                          ) : (
                            <input
                              type="number" min={0} value={row.target}
                              className="qm-td-input"
                              onChange={(e) => handleRowChange(idx, "target", e.target.value)}
                            />
                          )}
                        </td>

                        {/* Achieved count */}
                        <td style={{ textAlign: 'center' }}>
                          {achieved !== null ? (
                            <span style={{
                              fontWeight: 700,
                              fontSize: 13,
                              color: isFull ? '#15803d' : achieved > 0 ? '#0891b2' : '#9ca3af',
                            }}>
                              {achieved}
                            </span>
                          ) : (
                            <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                          )}
                        </td>

                        <td>
                          <select className="qm-action-select" value={row.action}
                            onChange={(e) => handleRowChange(idx, "action", e.target.value)}
                            style={{ border: `1.5px solid ${ac.border}`, background: ac.bg, color: ac.text }}>
                            {ACTION_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                        </td>
                        <td className="qm-td-remove">
                          <button className="qm-row-remove-btn" onClick={() => handleRemoveRow(idx)}>
                            <FontAwesomeIcon icon={faTrash} size="xs" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Add Row + Re-configure buttons */}
          <div style={{ display: "flex", gap: 8, marginTop: 10, marginBottom: 4 }}>
            <button className="qm-add-row-btn" onClick={handleAddRow}>
              <FontAwesomeIcon icon={faPlus} size="xs" /> Add Row
            </button>
            <button
              className="qm-reconfigure-btn"
              onClick={() => {
                if (rows.length && !window.confirm('Going back will keep existing rows but allow re-selecting variables. Continue?')) return;
                onChange({ ...quota, generated: false });
              }}
            >
              ← Re-configure Variables
            </button>
          </div>

          {/* Stats strip */}
          <div className="qm-stats-strip">
            {[
              ["Quota", name],
              ["Rows", rows.length],
              ["Variables", selectedVarIds.length],
              ["TERMINATE", rows.filter((r) => r.action === "TERMINATE").length],
              ["CONTINUE", rows.filter((r) => r.action === "CONTINUE").length],
              ["Total target", totalTarget],
              ["Achieved", Object.values(achievedData).reduce((s, v) => s + v, 0) || 0],
            ].map(([label, val]) => (
              <div key={label} className="qm-stat">
                <span className="qm-stat-label">{label}: </span>
                <span className="qm-stat-val">{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── QuotaModal ───────────────────────────────────────────────────────────────
const QuotaModal = ({ isOpen, onClose, onSave, initialData = null, sdataGroup = {}, projectId }) => {
  const [quotas, setQuotas] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [ready, setReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [anyGenerating, setAnyGenerating] = useState(false);
  const [anyGeneratingMsg, setAnyGeneratingMsg] = useState('');
  const [showImportExport, setShowImportExport] = useState(false);
  const fileInputRef = useRef(null);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);

  // Add this function to refresh all quotas
  const refreshAllAchieved = async () => {
    if (!projectId) return;
    setIsRefreshingAll(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/get-quota-tracking/${projectId}`, {
        credentials: 'include',
      });
      const result = await res.json();
      if (result.success) {
        // Create a map of quotaName -> achieved data
        const allAchievedMap = {};
        const tracking = result.tracking || {};

        Object.values(tracking).forEach(quotaGroup => {
          if (!quotaGroup) return;
          const trackRows = Array.isArray(quotaGroup.rows) ? quotaGroup.rows : [];
          const quotaKey = quotaGroup.quotaName || quotaGroup.name;
          if (!allAchievedMap[quotaKey]) allAchievedMap[quotaKey] = {};
          trackRows.forEach(row => {
            const key = row.name || row.logic;
            if (key) allAchievedMap[quotaKey][key] = row.count || row.achieved || 0;
          });
        });

        // Update each quota's achieved data through the editor's fetchAchieved
        // Since we can't directly access child component's state, we'll trigger a re-render
        // by updating a timestamp that child components can use as a dependency
        setRefreshTimestamp(Date.now());
      }
    } catch (e) {
      console.error('Refresh all failed:', e);
      alert('Failed to refresh achieved counts for all quotas');
    } finally {
      setIsRefreshingAll(false);
    }
  };

  // Add this state to trigger child refreshes
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());

  useEffect(() => {
    if (!isOpen) { setReady(false); return; }

    const loadData = async () => {
      if (projectId) {
        try {
          const res = await fetch(`${config.API_BASE_URL}/get-quota-config/${projectId}`, { credentials: 'include' });
          const result = await res.json();
          if (result.success && result.quotaData) {
            const mapQuota = (q) => ({
              name: q.quotaName || q.name || "Quota_1",
              quotaId: q.quotaId || null,
              selectedVarIds: q.selectedVarIds || [],
              rows: (q.rows || []).map(r => ({ name: r.name || '', logic: r.logic || '', target: r.target || 0, action: r.action || 'CONTINUE' })),
              generated: !!(q.rows?.length),
              autoName: q.autoName ?? true,
            });
            const loaded = (result.quotaData.quotas || []).map(mapQuota);
            setQuotas(loaded.length ? loaded : [{ name: "Quota_1", quotaId: null, selectedVarIds: [], rows: [], generated: false, autoName: true }]);
            setActiveIdx(0); setReady(true); return;
          }
        } catch (err) { console.error('DB load failed:', err); }
      }

      const stored = loadFromStorage();
      if (stored?.quotas?.length) {
        setQuotas(stored.quotas.map((q) => ({
          name: q.quotaName || q.name || "Quota_1",
          quotaId: q.quotaId || null,
          selectedVarIds: q.selectedVarIds || [],
          rows: q.rows || [],
          generated: !!(q.rows?.length),
          autoName: q.autoName ?? true,
        })));
        setActiveIdx(0); setReady(true); return;
      }

      if (initialData?.quotas?.length) {
        setQuotas(initialData.quotas.map((q) => ({
          name: q.quotaName || q.name || "Quota_1",
          quotaId: q.quotaId || null,
          selectedVarIds: q.selectedVarIds || [],
          rows: q.rows || [],
          generated: !!(q.rows?.length),
          autoName: q.autoName ?? true,
        })));
        setActiveIdx(0); setReady(true); return;
      }

      setQuotas([{ name: "Quota_1", quotaId: null, selectedVarIds: [], rows: [], generated: false, autoName: true }]);
      setActiveIdx(0); setReady(true);
    };

    loadData();
  }, [isOpen, projectId]);

  if (!isOpen) return null;

  if (!ready) {
    return (
      <div className="quota-modal-overlay">
        <div className="quota-modal-content">
          <div className="quota-modal-header">
            <div><h3>Quota Configuration</h3></div>
            <div className="header-actions">
              <button className="quota-modal-close-button" onClick={onClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <div className="quota-modal-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
            <div style={{ textAlign: 'center' }}>
              <div className="qm-spinner" />
              <p style={{ marginTop: 12, fontSize: 13, color: 'var(--color-gray-400)' }}>Loading quota configuration…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleExportQuota = () => {
    const generated = quotas.filter(q => q.rows?.length);
    if (!generated.length) { alert('No quota rows to export. Please generate quota configurations first.'); return; }
    const wb = XLSX.utils.book_new();
    generated.forEach((q) => {
      const data = q.rows.map((row, idx) => ({
        'Row #': idx + 1,
        'Row Name': row.name,
        'Logic': row.logic,
        'Target': row.target || 0,
        'Action': row.action || 'CONTINUE',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      // Column widths: Row#, Row Name, Logic, Target, Action
      ws['!cols'] = [{ wch: 6 }, { wch: 32 }, { wch: 32 }, { wch: 10 }, { wch: 12 }];
      // Excel sheet names: max 31 chars, no special chars
      const sheetName = q.name.replace(/[\\/*?:[\]]/g, '_').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = generated.length === 1
      ? `quota_${generated[0].name}_${dateStr}.xlsx`
      : `quota_config_${dateStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleImportQuota = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        if (!json?.length) { alert('No data found.'); return; }
        const q = quotas[activeIdx];
        if (!q) { alert('No active quota.'); return; }
        const targetMap = {};
        const actionMap = {};
        json.forEach(row => {
          [row['Row Name'], row['Logic']].filter(Boolean).forEach(key => {
            targetMap[key] = parseInt(row['Target']) || 0;
            const act = String(row['Action'] || '').toUpperCase().trim();
            if (ACTION_OPTIONS.includes(act)) actionMap[key] = act;
          });
        });
        const updated = { ...q, rows: q.rows.map(r => ({
          ...r,
          target: targetMap[r.name] ?? targetMap[r.logic] ?? r.target ?? 0,
          action: actionMap[r.name] ?? actionMap[r.logic] ?? r.action ?? 'CONTINUE',
        })) };
        const next = quotas.map((qt, i) => i === activeIdx ? updated : qt);
        setQuotas(next);
        if (fileInputRef.current) fileInputRef.current.value = '';
        alert(`✅ Imported! Updated targets & actions for ${updated.rows.length} rows.`);
      } catch (err) { alert(`❌ Import failed: ${err.message}`); }
    };
    reader.readAsArrayBuffer(file);
  };

  const addQuota = () => {
    const next = [...quotas, { name: `Quota_${quotas.length + 1}`, quotaId: null, selectedVarIds: [], rows: [], generated: false, autoName: true }];
    setQuotas(next);
    setActiveIdx(next.length - 1);
  };

  const removeQuota = (idx) => {
    const next = quotas.filter((_, i) => i !== idx);
    if (!next.length) {
      setQuotas([{ name: "Quota_1", quotaId: null, selectedVarIds: [], rows: [], generated: false, autoName: true }]);
      setActiveIdx(0); return;
    }
    setQuotas(next);
    setActiveIdx(Math.min(idx, next.length - 1));
  };

  const updateQuota = (idx, updated) => setQuotas((prev) => prev.map((q, i) => (i === idx ? updated : q)));

  const handleSave = async () => {
    const payload = {
      quotas: quotas.map((q) => ({
        quotaName: q.name,
        quotaId: q.quotaId || null,
        selectedVarIds: q.selectedVarIds,
        generated: q.generated,
        autoName: q.autoName,
        rows: q.rows.map(r => ({ name: r.name, logic: r.logic, target: r.target || 0, action: r.action || 'CONTINUE' })),
      })),
      nextQuotaNumber: quotas.length + 1,
    };
    setIsSaving(true);
    setIsSaved(false);
    let saved = false;
    try {
      if (projectId) {
        const res = await fetch(`${config.API_BASE_URL}/save-quota-config`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ projectId, quotaData: payload }),
        });
        const result = await res.json();
        if (!result.success) { alert(`❌ Save failed: ${result.error || 'Unknown error'}`); return; }
        // Re-fetch to get backend-assigned quotaIds for new quotas
        const fetchRes = await fetch(`${config.API_BASE_URL}/get-quota-config/${projectId}`, { credentials: 'include' });
        const fetchResult = await fetchRes.json();
        if (fetchResult.success && fetchResult.quotaData) {
          const updated = (fetchResult.quotaData.quotas || []).map((q) => ({
            name: q.quotaName || q.name || "Quota_1",
            quotaId: q.quotaId || null,
            selectedVarIds: q.selectedVarIds || [],
            rows: (q.rows || []).map(r => ({ name: r.name || '', logic: r.logic || '', target: r.target || 0, action: r.action || 'CONTINUE' })),
            generated: !!(q.rows?.length),
            autoName: q.autoName ?? true,
          }));
          if (updated.length) setQuotas(updated);
          saveToStorage(fetchResult.quotaData);
          onSave?.(fetchResult.quotaData);
        } else {
          onSave?.(payload);
        }
        saved = true;
      } else {
        saveToStorage(payload); onSave?.(payload);
        saved = true;
      }
    } catch (err) {
      console.error(err);
      alert('❌ Save error: ' + err.message);
    } finally {
      setIsSaving(false);
      if (saved) {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 3000);
      }
    }
  };

  const handleClear = () => {
    if (!window.confirm("Clear all saved quota data?")) return;
    localStorage.removeItem(STORAGE_KEY);
    setQuotas([{ name: "Quota_1", quotaId: null, selectedVarIds: [], rows: [], generated: false, autoName: true }]);
    setActiveIdx(0);
  };

  const allGenerated = quotas.every((q) => q.generated);
  const totalTarget = quotas.reduce((sum, q) => sum + (q.rows || []).reduce((s, r) => s + (r.target || 0), 0), 0);
  const isBusy = isSaving || anyGenerating;

  return (
    <div className="quota-modal-overlay">
      <div className="quota-modal-content">

        {/* ── In-modal overlay — locks the modal during save or generate ── */}
        {(isSaving || anyGenerating) && (
          <div className="qm-modal-loader">
            <div className="qm-spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
            <p className="qm-modal-loader-text">
              {isSaving
                ? `Saving ${quotas.length} quota${quotas.length !== 1 ? 's' : ''}…`
                : anyGeneratingMsg || 'Generating quota combinations…'}
            </p>
          </div>
        )}

        {/* ── Header ── */}
        <div className="quota-modal-header">
          <div>
            <h3>Quota Configuration</h3>
            <div className="quota-modal-header-sub">
              <span>{quotas.length} quota{quotas.length !== 1 ? "s" : ""}</span>
              <span className="header-separator">·</span>
              <span className="config-status">
                <span className="status-dot"></span>Config Ready
              </span>
              {/* {totalTarget > 0 && <span className="total-target-badge">Total: {totalTarget}</span>} */}
            </div>
          </div>
          <div className="header-actions">
            <button
              className="qm-header-btn"
              onClick={refreshAllAchieved}
              disabled={isRefreshingAll}
              title="Refresh achieved counts for all quotas"
            >
              <FontAwesomeIcon icon={faSync} spin={isRefreshingAll} />
              {isRefreshingAll ? " Refreshing All…" : " Refresh All"}
            </button>
            <button className="qm-header-btn" onClick={() => setShowImportExport(!showImportExport)} title="Import / Export">
              <FontAwesomeIcon icon={faFileImport} /> Import/Export
            </button>
            <button className="quota-modal-close-button" onClick={onClose} disabled={isBusy}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* ── Import/Export Panel ── */}
        {showImportExport && (
          <div className="qm-import-export-panel">
            <button className="qm-ie-btn export" onClick={handleExportQuota}>
              <FontAwesomeIcon icon={faFileExport} /> Export Quota (Excel)
            </button>
            <button className="qm-ie-btn import" onClick={() => fileInputRef.current?.click()}>
              <FontAwesomeIcon icon={faFileImport} /> Import Quota (Excel)
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportQuota} style={{ display: 'none' }} />
            <span className="qm-ie-hint">Excel columns: Row Name, Logic, Target</span>
          </div>
        )}

        {/* ── Tabs — key={i} to avoid clash on same name ── */}
        <div className="quota-tabs-bar">
          {quotas.map((q, i) => (
            <div
              key={i}
              className={`quota-tab ${activeIdx === i ? "active" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              <span>{q.name || `Quota ${i + 1}`}{q.rows?.length ? ` (${q.rows.length})` : ''}</span>
              <span className={`quota-tab-dot ${q.generated ? "generated" : ""}`} />
              {quotas.length > 1 && (
                <button className="quota-tab-remove" onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Remove quota "${q.name}"?`)) removeQuota(i);
                }}>×</button>
              )}
            </div>
          ))}
          <button className="quota-tab-add" onClick={addQuota}>
            <FontAwesomeIcon icon={faPlus} size="xs" /> New quota
          </button>
        </div>

        {/* ── Body ── */}
        <div className="quota-modal-body">
          {quotas[activeIdx] && (
            <QuotaEditor
              key={activeIdx}
              quota={quotas[activeIdx]}
              sdataGroup={sdataGroup}
              onChange={(updated) => updateQuota(activeIdx, updated)}
              onDelete={() => removeQuota(activeIdx)}
              showDelete={true}
              onGenerate={(updated) => updateQuota(activeIdx, updated)}
              projectId={projectId}
              onBusyChange={(busy, msg) => { setAnyGenerating(busy); setAnyGeneratingMsg(msg || ''); }}
              refreshTrigger={refreshTimestamp}
            />
          )}
        </div>

        {/* ── Footer ── */}
        <div className="quota-modal-footer">
          <button className="quota-clear-button" onClick={handleClear}>
            <FontAwesomeIcon icon={faTrash} size="xs" /> Clear saved data
          </button>
          <div className="qm-footer-right">
            {!allGenerated && <span className="qm-warn">⚠ Some quotas not yet generated</span>}
            <button className="quota-cancel-button" onClick={onClose} disabled={isBusy}>Cancel</button>
            <button className="quota-save-button" onClick={handleSave} disabled={isBusy}>
              {isSaving
                ? <><span className="qm-spinner-inline" /> Saving…</>
                : isSaved
                ? <>✓ Saved!</>
                : `Save All Quotas (${quotas.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaModal;